import React, { useRef, useState } from 'react'
import axios from 'axios'
import { FormikProps } from 'formik'
import ServoMoveStepForm from './ServoMoveStepForm'
import UsagePanel from './UsagePanel'
import AiSuggestionPanel from './AiSuggestionPanel'
import {
  admin_servo_move_step_comments_path,
  admin_servo_move_step_path,
  admin_servo_move_steps_path,
  copy_sources_admin_servo_move_steps_path,
  create_draft_from_version_admin_servo_move_step_path,
  create_new_version_draft_admin_servo_move_step_path,
  edit_admin_servo_move_step_path,
  publish_admin_servo_move_step_path,
  publish_and_sync_admin_servo_move_step_path,
  tag_options_admin_servo_move_step_comments_path,
} from '../../../routes'
import { t } from '../../i18n'
import CommentsPanel from '../shared/comments/CommentsPanel'
import { renderRatingMeta } from '../shared/comments/RatingSummary'
import { CommentsSection } from '../shared/comments/types'
import extractServerErrors from '../shared/extractServerErrors'
import SectionTitle from '../shared/sectionTitle'
import StatusBadge from '../shared/StatusBadge'
import ConfirmModal from '../shared/versioning/ConfirmModal'
import CopyFromModal from '../shared/versioning/CopyFromModal'
import PublishConfirmModal from '../shared/versioning/PublishConfirmModal'
import PublishImpactBody from '../shared/versioning/PublishImpactBody'
import ReadOnlyNotice from '../shared/versioning/ReadOnlyNotice'
import VersionActionsBar from '../shared/versioning/VersionActionsBar'
import VersionsPanel from '../shared/versioning/VersionsPanel'
import { VersionStatusBadge } from '../shared/versioning/badges'
import useCopyFromSource from '../shared/versioning/useCopyFromSource'
import useSaveAndPublish from '../shared/versioning/useSaveAndPublish'
import {
  CopySourceServoMoveStep,
  MoveStepType,
  ServoMoveStep,
  ServoMoveStepUsage,
  VersionRow,
  VersioningProps,
  syncBadge,
} from './models'

const emptyMoveStep: ServoMoveStep = {
  id: null,
  name: '',
  move_step_type: 'cooking_mode' as MoveStepType,
  number_of_moves: 0,
  servo_moves: Array(1).fill({
    position: 0,
    velocity: 0,
    acceleration: 0,
    deceleration: 0,
    pause_time: 0,
    step_number: 1
  }),
}

const ServoMoveStepPage: React.FC<{
  servoMoveStep: ServoMoveStep
  readOnly?: boolean
  commentsSection?: CommentsSection
  versioning?: VersioningProps
  aiSuggestionsEnabled?: boolean
  usage?: ServoMoveStepUsage
}> = ({
  servoMoveStep: initialServoMoveStep = null,
  readOnly = false,
  commentsSection,
  versioning,
  aiSuggestionsEnabled = false,
  usage,
}) => {

  const [servoMoveStep, setServoMoveStep] = useState(initialServoMoveStep || emptyMoveStep)
  const [saveSuccessful, setSaveSuccessful] = useState<boolean | null>(null)
  const [saveText, setSaveText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const formikRef = useRef<FormikProps<ServoMoveStep>>(null)
  const { confirmPublish, afterSaved, cancelPending } = useSaveAndPublish<ServoMoveStep>({
    formikRef,
    publishUrl: publish_admin_servo_move_step_path,
    publishAndSyncUrl: publish_and_sync_admin_servo_move_step_path,
    publishFailedText: t('versioning.publish_failed'),
    onPublishError: (messages) => {
      setSubmitting(false)
      setSaveSuccessful(false)
      setSaveText(messages.join(', '))
    }
  })

  const handleSubmit = (servoMoveStep, onFinished) => {
    const isCreate = !servoMoveStep.id
    const { servo_moves, ...moveStep } = servoMoveStep
    const body = {servo_move_step: {...moveStep, servo_moves_attributes: servo_moves}}
    setSubmitting(true)
    const request = servoMoveStep.id
      ? axios.put(admin_servo_move_step_path(servoMoveStep.id), body)
      : axios.post(admin_servo_move_steps_path(), body)
    request
      .then((resp) => {
        if (isCreate) {
          window.location.href = edit_admin_servo_move_step_path(resp.data.id)
          return
        }
        setSaveSuccessful(true)
        setSaveText(t('save_successful', { scope: 'servo_move_steps' }))
        onFinished(true, null)
        setServoMoveStep(resp.data)
        if (!afterSaved(resp.data.id)) setSubmitting(false)
      })
      .catch((error) => {
        cancelPending()
        setSubmitting(false)
        setSaveSuccessful(false)
        const data = axios.isAxiosError(error) ? error.response?.data : null
        setSaveText(extractServerErrors(error, t('save_failed', { scope: 'servo_move_steps' })).join(', '))
        onFinished(false, { full_errors: data?.full_errors ?? {} })
      })
  }

  // Existing moves are marked for destruction rather than dropped, so the update request
  // deletes them; the copied moves arrive without ids and are created
  const applyCopiedMoves = (source: CopySourceServoMoveStep) => {
    const formik = formikRef.current
    if (!formik) return

    const removed = formik.values.servo_moves
      .filter((move) => move.id)
      .map((move) => ({ ...move, _destroy: true }))
    const copied = [...source.servo_moves]
      .sort((a, b) => a.step_number - b.step_number)
      .map(({ position, velocity, acceleration, deceleration, pause_time }, index) => ({
        position, velocity, acceleration, deceleration, pause_time, step_number: index + 1, _destroy: false
      }))
    formik.setFieldValue('servo_moves', [...copied, ...removed])
    formik.setFieldValue('number_of_moves', copied.length)
  }
  const copyFrom = useCopyFromSource(formikRef, applyCopiedMoves)

  const renderSaveMessage = () => {
    if (saveText) {
      return (
        <div
          className={`alert alert-${saveSuccessful ? 'success' : 'danger'}`}
          role="alert"
        >
          {saveText}
        </div>
      )
    }
  }

  const renderVersionBadges = (version: VersionRow) =>
    syncBadge(version.syncedWithRobot) && (
      <span className="ms-1">
        <StatusBadge {...syncBadge(version.syncedWithRobot)!} />
      </span>
    )

  const openPublishConfirm = () => setShowPublishConfirm(true)
  const publishWith = (mode: 'publish' | 'publish_and_sync') => {
    setShowPublishConfirm(false)
    confirmPublish(mode)
  }

  return (
    <>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <SectionTitle rows={[!!servoMoveStep.id ? 'Edit': 'New', t('servo_move_steps.servo_move_step')]} />
          {servoMoveStep.status && (
            <span className="ms-3">
              <VersionStatusBadge status={servoMoveStep.status} versionNumber={servoMoveStep.version_number} />
            </span>
          )}
          {syncBadge(servoMoveStep.synced_with_robot) && (
            <span className="ms-2">
              <StatusBadge {...syncBadge(servoMoveStep.synced_with_robot)!} />
            </span>
          )}
        </div>
        <VersionActionsBar
          actions={versioning?.actions}
          readOnly={readOnly}
          submitting={submitting}
          formId="servo-move-step-form"
          indexHref={admin_servo_move_steps_path()}
          deleteDraftUrl={servoMoveStep.id ? admin_servo_move_step_path(servoMoveStep.id) : undefined}
          createNewVersionUrl={
            servoMoveStep.id ? create_new_version_draft_admin_servo_move_step_path(servoMoveStep.id) : undefined
          }
          deleteConfirmBody={t('servo_move_steps.delete_draft_confirm_body')}
          onPublish={openPublishConfirm}
        >
          {(versioning?.actions.canCopy ?? true) && (
            <button type="button" className="btn btn-secondary inverted me-2" onClick={copyFrom.openPicker}>
              {t('servo_move_steps.copy_from_step.button')}
            </button>
          )}
        </VersionActionsBar>
      </div>
      {readOnly && servoMoveStep.status && <ReadOnlyNotice />}
      <div className="card card-robis">
        <div className="servo-move-step-layout">
          <div className="servo-move-step-layout__form">
            {renderSaveMessage()}
            <ServoMoveStepForm
              servoMoveStep={servoMoveStep}
              onSubmitForm={handleSubmit}
              readOnly={readOnly}
              formikRef={formikRef}
            />
          </div>
          {servoMoveStep.id && (versioning || commentsSection) && (
            <div className="servo-move-step-layout__comments">
              <div className="servo-move-step-layout__comments-inner">
                {aiSuggestionsEnabled && servoMoveStep.status === 'published' && (
                  <AiSuggestionPanel servoMoveStepId={servoMoveStep.id} />
                )}
                {versioning && (
                  <VersionsPanel
                    versions={versioning.versions}
                    canRestore={versioning.actions.canRestore}
                    versionHref={(version) => edit_admin_servo_move_step_path(version.id)}
                    restoreUrl={(version) => create_draft_from_version_admin_servo_move_step_path(version.id)}
                    renderBadges={renderVersionBadges}
                    renderMeta={renderRatingMeta}
                  />
                )}
                {commentsSection && (
                  <CommentsPanel
                    commentsUrl={admin_servo_move_step_comments_path(servoMoveStep.id)}
                    tagOptionsUrl={tag_options_admin_servo_move_step_comments_path(servoMoveStep.id)}
                    initialComments={commentsSection.comments}
                    abilities={commentsSection.abilities}
                    tagging={commentsSection.tagging}
                    placeholder={t('servo_move_steps.comment_placeholder')}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {servoMoveStep.id && usage && <UsagePanel usage={usage} />}
      {copyFrom.pickerOpen && (
        <CopyFromModal<CopySourceServoMoveStep>
          url={copy_sources_admin_servo_move_steps_path()}
          params={{
            move_step_type: servoMoveStep.move_step_type,
            exclude_servo_move_step_id: servoMoveStep.id ?? null
          }}
          scope="servo_move_steps.copy_from_step"
          renderMeta={(source) =>
            t('servo_move_steps.copy_from_step.moves', { count: source.servo_moves.length })
          }
          onSelect={copyFrom.handlePicked}
          onClose={copyFrom.closePicker}
        />
      )}
      {copyFrom.overwriteSource && (
        <ConfirmModal
          title={t('servo_move_steps.copy_from_step.overwrite_title')}
          confirmActions={[
            {
              label: t('servo_move_steps.copy_from_step.overwrite_confirm'),
              className: 'btn btn-primary',
              onConfirm: copyFrom.confirmOverwrite,
            },
          ]}
          onClose={copyFrom.cancelOverwrite}
        >
          <p className="mb-0">{t('servo_move_steps.copy_from_step.overwrite_text')}</p>
        </ConfirmModal>
      )}
      {showPublishConfirm && versioning && (
        <PublishConfirmModal
          onPublish={() => publishWith('publish')}
          onPublishAndSync={() => publishWith('publish_and_sync')}
          onClose={() => setShowPublishConfirm(false)}
        >
          <PublishImpactBody
            usedIntro={
              <>
                {t('servo_move_steps.publish_confirm_used_prefix')}
                <strong>{t('servo_move_steps.publish_confirm_used_bold')}</strong>:
              </>
            }
            usedItems={versioning.usedByMoveModes}
            noUsageText={t('servo_move_steps.publish_confirm_no_modes')}
            locationsIntro={t('servo_move_steps.publish_confirm_locations')}
            locations={versioning.affectedLocations}
            noLocationsText={t('servo_move_steps.publish_confirm_no_locations')}
          />
        </PublishConfirmModal>
      )}
    </>
  )
}

export default ServoMoveStepPage
